/**
 * The child's side of "Send to all" — homework their teachers actually sent.
 *
 * Two shapes of one read: a one-line banner on Today, so the child sees there
 * is something the moment they land, and the full list on Assigned papers,
 * next to the papers from the same teachers. Neither renders anything until
 * a teacher has sent a list — a card saying "no homework" is noise on the
 * page a child opens every day.
 *
 * The list is the teacher's, as she left it: her note, her order (Blue, then
 * Orange, then Pink), and a link on each item that opens where the work is.
 * Every link was built by the server when the list was generated.
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api';
import { binRamp } from '@/lib/bins';
import { BinGlyph } from '@/components/ds';
import { DS_BIN, fmtDate } from '@/components/reports/ActionPlanKit';
import type { StudentHomework, StudentHomeworkItem } from '@cwy/config/contracts';

type Sent = StudentHomework;

function useHomework() {
  return useQuery({
    queryKey: ['my-homework'],
    queryFn: () => apiGet<Sent[]>('/me/homework'),
  });
}

const minutes = (h: Sent) => h.items.reduce((s, i) => s + i.estMinutes, 0);

/** On Today: that there is homework, from whom, and how long. */
export function HomeworkBanner() {
  const { data } = useHomework();
  const latest = data?.[0];
  if (!latest) return null;
  return (
    <Link
      to="/tests/assigned"
      className="flex flex-wrap items-center justify-between gap-2 rounded-2xl px-4 py-3 no-underline"
      style={{ background: 'var(--brand-lime-soft)', border: '1px solid var(--brand-ink)' }}
    >
      <span className="text-sm" style={{ color: 'var(--brand-ink)' }}>
        <strong>Homework from {latest.teacherName}</strong> · {latest.items.length}{' '}
        {latest.items.length === 1 ? 'thing' : 'things'} · about {minutes(latest)} min
        {data.length > 1 && ` · ${data.length - 1} more list${data.length > 2 ? 's' : ''}`}
      </span>
      <span className="text-xs font-bold" style={{ color: 'var(--brand-ink)' }}>
        Open →
      </span>
    </Link>
  );
}

function Item({ item }: { item: StudentHomeworkItem }) {
  const ramp = binRamp(item.band);
  const body = (
    <>
      <span className="flex-none" style={{ color: ramp.text }}>
        <BinGlyph bin={DS_BIN[item.band]} size={9} color="currentColor" title={null} />
      </span>
      <span className="flex-1 text-sm font-semibold" style={{ color: 'var(--text-body)' }}>
        {item.label}
      </span>
      <span className="flex-none text-xs" style={{ color: 'var(--ink-3)' }}>
        {item.estMinutes} min {item.external ? '↗' : '→'}
      </span>
    </>
  );
  const cls = 'flex items-center gap-2.5 rounded-lg px-3 py-2 no-underline hover:opacity-80';
  const style = { background: 'var(--surface-card)', border: '1px solid var(--border-default)' };
  return item.external ? (
    <a href={item.href} target="_blank" rel="noreferrer" className={cls} style={style}>
      {body}
    </a>
  ) : (
    <Link to={item.href} className={cls} style={style}>
      {body}
    </Link>
  );
}

/** On Assigned papers: every list, newest first, with the note on top. */
export function HomeworkList() {
  const { data } = useHomework();
  if (!data?.length) return null;
  return (
    <section className="flex flex-col gap-3" aria-label="Homework from your teachers">
      {data.map((h) => (
        <div
          key={h.homeworkId}
          className="rounded-2xl p-4"
          style={{ background: 'var(--paper-sunk)', border: '1px solid var(--border-default)' }}
        >
          <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-sm font-bold" style={{ color: 'var(--text-strong)' }}>
              Homework · {h.paperTitle}
            </p>
            <p className="text-xs" style={{ color: 'var(--ink-3)' }}>
              From {h.teacherName} · {fmtDate(h.sentAt)} · about {minutes(h)} min
            </p>
          </div>
          {h.message && (
            <p className="mb-3 text-sm" style={{ color: 'var(--ink-2)' }}>
              {h.message}
            </p>
          )}
          <div className="flex flex-col gap-1.5">
            {h.items.map((item, i) => (
              <Item key={`${item.href}-${i}`} item={item} />
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}
